/**
 * Schedule Store — Weekly schedule + availability
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { supabase } from '../lib/supabase';
import { salesforceClient } from '../lib/salesforce/client';
import type { RouteListItem } from '../lib/salesforce/types';
import type { Staff, TimeEntry, LeaveEntry } from './workforce-store';
import { staffRijMatchtSleutel, staffSleutel } from '../lib/staff-sleutel';

// Types
export interface ScheduleEntry {
  id: string;
  staff_id: string | null;
  date: string;
  shift_type: 'wasserij' | 'route' | 'kantoor' | null;
  time_from: string;
  time_to: string;
  salesforce_route_id: string | null;
  notes: string | null;
  staff?: { name: string; team: string | null; contract_type: string };
}

export interface AvailabilityEntry {
  id: string;
  staff_id: string;
  date: string;
  is_available: boolean;
  time_from: string | null;
  time_to: string | null;
  notes: string | null;
}

export type ShiftType = 'wasserij' | 'route' | 'kantoor';

interface ScheduleState {
  // Data
  schedule: ScheduleEntry[];
  availability: AvailabilityEntry[];
  staffList: Staff[];
  sfRoutes: RouteListItem[];
  timeEntries: TimeEntry[];
  leaveEntries: LeaveEntry[];

  // Current week
  weekStart: string; // ISO date (Monday)
  weekDates: string[]; // Mon-Sat ISO dates

  // UI state
  isLoading: boolean;
  isSaving: boolean;
  isAssigning: boolean;
  assignError: string | null;
  selectedCell: { staffId: string; date: string } | null;
  teamFilter: 'all' | ShiftType;
  editMode: 'rooster' | 'beschikbaarheid';

  // Actions
  setWeek: (date: string) => void;
  goToNextWeek: () => void;
  goToPreviousWeek: () => void;
  goToThisWeek: () => void;
  fetchWeekData: () => Promise<void>;
  fetchSfRoutes: () => Promise<void>;
  addShift: (staffId: string, date: string, shiftType: ShiftType, timeFrom: string, timeTo: string) => Promise<boolean>;
  addOpenShift: (date: string, shiftType: ShiftType, timeFrom: string, timeTo: string) => Promise<boolean>;
  assignShift: (shiftId: string, staffId: string) => Promise<boolean>;
  updateShift: (id: string, updates: Partial<ScheduleEntry>) => Promise<boolean>;
  removeShift: (id: string) => Promise<boolean>;
  assignRoute: (routeId: string, staffId: string, date: string) => Promise<boolean>;
  unassignRoute: (routeId: string) => Promise<boolean>;
  toggleAvailability: (staffId: string, date: string) => Promise<boolean>;
  setSelectedCell: (cell: { staffId: string; date: string } | null) => void;
  setTeamFilter: (filter: 'all' | ShiftType) => void;
  setEditMode: (mode: 'rooster' | 'beschikbaarheid') => void;
}

function getMonday(dateStr: string): Date {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(date);
  monday.setDate(date.getDate() + offset);
  return monday;
}

function getWeekDates(mondayStr: string): string[] {
  const monday = new Date(mondayStr + 'T00:00:00');
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return formatDate(d);
  });
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string, options: RequestInit = {}) {
  const token = await getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return response.json();
}

const today = new Date();
const monday = getMonday(formatDate(today));
const initialWeekStart = formatDate(monday);

export const useScheduleStore = create<ScheduleState>()((set, get) => ({
  schedule: [],
  availability: [],
  staffList: [],
  sfRoutes: [],
  timeEntries: [],
  leaveEntries: [],
  weekStart: initialWeekStart,
  weekDates: getWeekDates(initialWeekStart),
  isLoading: false,
  isSaving: false,
  isAssigning: false,
  assignError: null,
  selectedCell: null,
  teamFilter: 'all',
  editMode: 'rooster',

  setWeek: (date) => {
    const monday = getMonday(date);
    const weekStart = formatDate(monday);
    set({ weekStart, weekDates: getWeekDates(weekStart) });
    get().fetchWeekData();
  },

  goToNextWeek: () => {
    const current = new Date(get().weekStart + 'T00:00:00');
    current.setDate(current.getDate() + 7);
    get().setWeek(formatDate(current));
  },

  goToPreviousWeek: () => {
    const current = new Date(get().weekStart + 'T00:00:00');
    current.setDate(current.getDate() - 7);
    get().setWeek(formatDate(current));
  },

  goToThisWeek: () => {
    get().setWeek(formatDate(new Date()));
  },

  fetchWeekData: async () => {
    set({ isLoading: true });
    try {
      const { weekStart, weekDates } = get();
      const endDate = weekDates[weekDates.length - 1] || weekStart;
      const [scheduleData, availabilityData, staffData, sfRoutesData, timeEntriesData, leaveData] = await Promise.all([
        apiFetch(`/api/workforce/schedule?week=${weekStart}`),
        apiFetch(`/api/workforce/availability?week=${weekStart}`),
        apiFetch('/api/workforce/staff'),
        salesforceClient.getCalendarRoutes(weekStart, endDate).catch((err) => {
          console.error('[Schedule] fetchSfRoutes error:', err);
          return [] as RouteListItem[];
        }),
        apiFetch(`/api/workforce/time-entries?from=${weekStart}&to=${endDate}`).catch(() => [] as TimeEntry[]),
        apiFetch(`/api/workforce/leave?from=${weekStart}&to=${endDate}`).catch(() => [] as LeaveEntry[]),
      ]);

      // Sync route planned times to schedule entries. Sinds de levende route
      // (plan 2026-09-06) bestaan die tijden ook vóór de lock en bewegen ze
      // mee met elke stop; de dienst volgt de route, nooit andersom.
      const updatedSchedule = (scheduleData as ScheduleEntry[]).map(entry => {
        if (!entry.salesforce_route_id) return entry;
        const route = (sfRoutesData as RouteListItem[]).find(r => r.id === entry.salesforce_route_id);
        if (!route?.startTimePlanned || !route.endTimePlanned) return entry;
        // Only update if times differ from planned
        if (entry.time_from?.slice(0, 5) !== route.startTimePlanned || entry.time_to?.slice(0, 5) !== route.endTimePlanned) {
          // Fire-and-forget update to Supabase
          apiFetch(`/api/workforce/schedule?id=${entry.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ time_from: route.startTimePlanned, time_to: route.endTimePlanned }),
          }).catch(() => {});
          return { ...entry, time_from: route.startTimePlanned, time_to: route.endTimePlanned };
        }
        return entry;
      });

      // Auto-create schedule entries for SF routes assigned to a chauffeur
      // but missing from the schedule (e.g. assigned directly in Salesforce)
      const sfRoutes = sfRoutesData as RouteListItem[];
      const staffArr = staffData as Staff[];
      const assignedWithoutEntry = sfRoutes.filter(route => {
        if (!route.chauffeurId) return false;
        return !updatedSchedule.some(s => s.salesforce_route_id === route.id);
      });

      for (const route of assignedWithoutEntry) {
        const matchedStaff = staffArr.find(s => staffRijMatchtSleutel(s, route.chauffeurId!));
        if (!matchedStaff) continue;

        const timeFrom = route.startTimePlanned || '06:00';
        const timeTo = route.endTimePlanned || '16:30';

        // Fire-and-forget: create the schedule entry in Supabase
        apiFetch('/api/workforce/schedule', {
          method: 'POST',
          body: JSON.stringify({
            staff_id: matchedStaff.id,
            date: route.datum,
            shift_type: 'route',
            time_from: timeFrom,
            time_to: timeTo,
            salesforce_route_id: route.id,
          }),
        }).catch(err => console.error('[Schedule] Auto-create route entry error:', err));

        // Add to local schedule immediately so it shows in the grid
        updatedSchedule.push({
          id: `auto-${route.id}`,
          staff_id: matchedStaff.id,
          date: route.datum,
          shift_type: 'route',
          time_from: timeFrom,
          time_to: timeTo,
          salesforce_route_id: route.id,
          notes: null,
          staff: { name: matchedStaff.name, team: matchedStaff.team, contract_type: matchedStaff.contract_type },
        });
      }

      set({
        schedule: updatedSchedule,
        availability: availabilityData,
        staffList: staffData,
        sfRoutes: sfRoutesData,
        timeEntries: timeEntriesData,
        leaveEntries: leaveData,
        isLoading: false,
      });
    } catch (error) {
      console.error('[Schedule] fetchWeekData error:', error);
      set({ isLoading: false });
    }
  },

  fetchSfRoutes: async () => {
    try {
      const { weekStart, weekDates } = get();
      const endDate = weekDates[weekDates.length - 1] || weekStart;
      const sfRoutesData = await salesforceClient.getCalendarRoutes(weekStart, endDate);
      set({ sfRoutes: sfRoutesData });
    } catch (error) {
      console.error('[Schedule] fetchSfRoutes error:', error);
    }
  },

  addShift: async (staffId, date, shiftType, timeFrom, timeTo) => {
    set({ isSaving: true });
    try {
      await apiFetch('/api/workforce/schedule', {
        method: 'POST',
        body: JSON.stringify({
          staff_id: staffId,
          date,
          shift_type: shiftType,
          time_from: timeFrom,
          time_to: timeTo,
        }),
      });
      await get().fetchWeekData();
      set({ isSaving: false, selectedCell: null });
      return true;
    } catch (error) {
      console.error('[Schedule] addShift error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  addOpenShift: async (date, shiftType, timeFrom, timeTo) => {
    set({ isSaving: true });
    try {
      await apiFetch('/api/workforce/schedule', {
        method: 'POST',
        body: JSON.stringify({
          date,
          shift_type: shiftType,
          time_from: timeFrom,
          time_to: timeTo,
        }),
      });
      await get().fetchWeekData();
      set({ isSaving: false });
      return true;
    } catch (error) {
      console.error('[Schedule] addOpenShift error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  assignShift: async (shiftId, staffId) => {
    set({ isSaving: true });
    try {
      await apiFetch(`/api/workforce/schedule?id=${shiftId}`, {
        method: 'PATCH',
        body: JSON.stringify({ staff_id: staffId }),
      });
      await get().fetchWeekData();
      set({ isSaving: false });
      return true;
    } catch (error) {
      console.error('[Schedule] assignShift error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  updateShift: async (id, updates) => {
    set({ isSaving: true });
    try {
      await apiFetch(`/api/workforce/schedule?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await get().fetchWeekData();
      set({ isSaving: false });
      return true;
    } catch (error) {
      console.error('[Schedule] updateShift error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  removeShift: async (id) => {
    set({ isSaving: true });
    try {
      await apiFetch(`/api/workforce/schedule?id=${id}`, { method: 'DELETE' });
      await get().fetchWeekData();
      set({ isSaving: false });
      return true;
    } catch (error) {
      console.error('[Schedule] removeShift error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  assignRoute: async (routeId, staffId, date) => {
    set({ isAssigning: true, assignError: null });
    try {
      const { staffList } = get();
      const staff = staffList.find(s => s.id === staffId);
      if (!staff) {
        set({ isAssigning: false, assignError: 'Medewerker niet gevonden' });
        return false;
      }

      // 1. Route toewijzen op de staff-sleutel. Tot 08-09 weigerde dit een
      // medewerker zonder sf_medewerker_id; sinds de cut krijgt niemand dat
      // veld nog, dus dat maakte elke nieuwe collega onplanbaar.
      await salesforceClient.updateRoute(routeId, { chauffeurId: staffSleutel(staff) });

      // 2. Remove any existing schedule entry for this route (in case of reassign)
      const { schedule } = get();
      const existingEntry = schedule.find(s => s.salesforce_route_id === routeId);
      if (existingEntry) {
        await apiFetch(`/api/workforce/schedule?id=${existingEntry.id}`, { method: 'DELETE' });
      }

      // 3. Create schedule entry in Supabase
      await apiFetch('/api/workforce/schedule', {
        method: 'POST',
        body: JSON.stringify({
          staff_id: staffId,
          date,
          shift_type: 'route',
          time_from: '06:00',
          time_to: '16:30',
          salesforce_route_id: routeId,
        }),
      });

      // 4. Refresh all data
      await get().fetchWeekData();
      set({ isAssigning: false, assignError: null });
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Onbekende fout bij toewijzen';
      console.error('[Schedule] assignRoute error:', error);
      set({ isAssigning: false, assignError: msg });
      return false;
    }
  },

  unassignRoute: async (routeId) => {
    set({ isAssigning: true, assignError: null });
    try {
      // 1. Clear medewerker in SF
      await salesforceClient.updateRoute(routeId, { chauffeurId: null });

      // 2. Find and delete schedule entry
      const { schedule } = get();
      const entry = schedule.find(s => s.salesforce_route_id === routeId);
      if (entry) {
        await apiFetch(`/api/workforce/schedule?id=${entry.id}`, { method: 'DELETE' });
      }

      // 3. Refresh all data
      await get().fetchWeekData();
      set({ isAssigning: false, assignError: null });
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Onbekende fout bij ontkoppelen';
      console.error('[Schedule] unassignRoute error:', error);
      set({ isAssigning: false, assignError: msg });
      return false;
    }
  },

  toggleAvailability: async (staffId, date) => {
    set({ isSaving: true });
    try {
      const { availability } = get();
      const existing = availability.find(a => a.staff_id === staffId && a.date === date);

      // Het dagdeel dat de medewerker zelf opgaf blijft staan als kantoor de
      // vlag omzet. De upsert gaat op (staff_id, date) en maakt van een
      // weggelaten veld NULL, dus de tijden moeten expliciet mee.
      const venster = {
        time_from: existing?.time_from ?? null,
        time_to: existing?.time_to ?? null,
      };

      if (existing?.is_available) {
        // available → not available
        await apiFetch('/api/workforce/availability', {
          method: 'PUT',
          body: JSON.stringify({ staff_id: staffId, entries: [{ date, is_available: false, ...venster }] }),
        });
      } else if (existing && !existing.is_available) {
        // not available → remove (unset)
        await apiFetch(`/api/workforce/availability?id=${existing.id}`, { method: 'DELETE' });
      } else {
        // unset → available
        await apiFetch('/api/workforce/availability', {
          method: 'PUT',
          body: JSON.stringify({ staff_id: staffId, entries: [{ date, is_available: true, ...venster }] }),
        });
      }

      await get().fetchWeekData();
      set({ isSaving: false });
      return true;
    } catch (error) {
      console.error('[Schedule] toggleAvailability error:', error);
      set({ isSaving: false });
      return false;
    }
  },

  setSelectedCell: (cell) => set({ selectedCell: cell }),
  setTeamFilter: (filter) => set({ teamFilter: filter }),
  setEditMode: (mode) => set({ editMode: mode }),
}));

// ============================================================================
// SELECTORS
// ============================================================================

export interface BijrijderAssignment {
  staffId: string;
  date: string;
  routeId: string;
  routeName: string;
  chauffeurName: string | null;
}

/**
 * Hook: virtuele bijrijder-toewijzingen afgeleid uit sfRoutes + staffList.
 * Geen schaduw-state in Supabase — Routes__c.Bijrijder__c blijft single source of truth.
 */
export function useBijrijderAssignments(): BijrijderAssignment[] {
  const { staffList, sfRoutes } = useScheduleStore(
    useShallow((state) => ({ staffList: state.staffList, sfRoutes: state.sfRoutes }))
  );

  return useMemo(() => {
    const byMedewerker = new Map<string, string>();
    staffList.forEach((s) => {
      byMedewerker.set(staffSleutel(s), s.id);
    });

    const result: BijrijderAssignment[] = [];
    for (const route of sfRoutes) {
      if (!route.bijrijderId) continue;
      const staffId = byMedewerker.get(route.bijrijderId);
      if (!staffId) continue;
      result.push({
        staffId,
        date: route.datum,
        routeId: route.id,
        routeName: route.routeName ?? route.name,
        chauffeurName: route.chauffeurName,
      });
    }
    return result;
  }, [staffList, sfRoutes]);
}
